// Line-of-sight occlusion: does a straight sightline from an observer to a
// target pass through a building footprint? Used to let units hide behind
// buildings from enemy vision.
//
// Segment-segment intersection is affine-invariant, and the lon/lat -> local
// meters projection is approximately affine over the small distances a
// tactical zone spans, so the crossing tests run directly in raw lon/lat
// coordinates without projecting to meters first. Building footprints come in
// as GeoJSON rings (from building-geojson.mjs), which this module treats as
// occluding polygons.

function orientation(a, b, c) {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
  if (Math.abs(value) < 1e-15) return 0;
  return value > 0 ? 1 : 2;
}

function onSegment(a, b, c) {
  return Math.min(a[0], c[0]) <= b[0] && b[0] <= Math.max(a[0], c[0])
    && Math.min(a[1], c[1]) <= b[1] && b[1] <= Math.max(a[1], c[1]);
}

export function segmentsIntersect(p1, p2, p3, p4) {
  const o1 = orientation(p1, p2, p3);
  const o2 = orientation(p1, p2, p4);
  const o3 = orientation(p3, p4, p1);
  const o4 = orientation(p3, p4, p2);
  if (o1 !== o2 && o3 !== o4) return true;
  // Colinear touching cases.
  if (o1 === 0 && onSegment(p1, p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p2, p4)) return true;
  return false;
}

function pointInRing(point, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects = (yi > point[1]) !== (yj > point[1])
      && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

// A sightline crosses a ring if it intersects any edge, or if it lies wholly
// inside the ring (endpoints inside, no edge crossing — e.g. both observer and
// target within the same building).
export function segmentCrossesRing(from, to, ring) {
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    if (segmentsIntersect(from, to, ring[j], ring[i])) return true;
  }
  return pointInRing(from, ring) || pointInRing(to, ring);
}

// Exported so other geometry-heavy domain modules (e.g. vision-cone.mjs's
// occluded cone raycast) can walk building rings without duplicating this
// Polygon/MultiPolygon unwrap.
export function ringsOf(feature) {
  const geometry = feature?.geometry;
  if (!geometry) return [];
  if (geometry.type === "Polygon") return [geometry.coordinates[0]];
  if (geometry.type === "MultiPolygon") return geometry.coordinates.map((polygon) => polygon[0]);
  return [];
}

// True when any occluding building blocks the straight sightline from -> to.
// occluders is an array of GeoJSON building features (Polygon/MultiPolygon).
export function sightLineBlocked(from, to, occluders) {
  if (!Array.isArray(occluders)) return false;
  for (const feature of occluders) {
    for (const ring of ringsOf(feature)) {
      if (ring.length >= 3 && segmentCrossesRing(from, to, ring)) return true;
    }
  }
  return false;
}

// True when a coordinate falls inside any building footprint — used by the
// nav grid to mark building cells impassable so units path around, not through.
export function pointInBuildings(coordinate, buildings) {
  if (!Array.isArray(buildings)) return false;
  for (const feature of buildings) {
    for (const ring of ringsOf(feature)) {
      if (ring.length >= 3 && pointInRing(coordinate, ring)) return true;
    }
  }
  return false;
}
